import { z } from 'zod';
import { PrioritaTask, StatoTask } from './enums';
import { dataOraSchema, idSchema, idSelezioneSchema, paginationQuerySchema } from '../common';

/**
 * Il task è autonomo: può esistere senza progetto. Resta distinto dalla
 * scadenza di progetto — per scelta di prodotto i due concetti non si unificano.
 */

const taskBaseSchema = z.object({
  titolo: z.string().trim().min(1).max(255),
  descrizione: z.string().trim().max(10_000).optional(),
  /** Uno e un solo assegnatario, opzionale: alimenta "i miei task". */
  assegnatarioId: idSelezioneSchema('Seleziona un assegnatario').optional(),
  progettoId: idSelezioneSchema('Seleziona un progetto').optional(),
  /** Ammessa solo con un progetto e solo se la fase appartiene a quel progetto. */
  faseProgettoId: idSelezioneSchema('Seleziona una fase').optional(),
  dataInizio: dataOraSchema.optional(),
  /** Obbligatoria: un task senza scadenza non è ammesso. */
  dataScadenza: dataOraSchema,
  /** `false` quando l'orario è significativo; i task all-day stanno in cima al giorno. */
  scadenzaConOrario: z.boolean().default(false),
  stato: StatoTask.default('Da fare'),
  priorita: PrioritaTask.optional(),
});

/** Coerenza task: la fase richiede il progetto. L'appartenenza effettiva
 *  della fase al progetto va verificata a livello di service (serve il DB). */
export const taskCreateSchema = taskBaseSchema.superRefine((t, ctx) => {
  if (t.faseProgettoId != null && t.progettoId == null) {
    ctx.addIssue({
      code: 'custom',
      message: 'La fase può essere indicata solo se il task è collegato a un progetto',
      path: ['faseProgettoId'],
    });
  }
  if (t.dataInizio != null && t.dataInizio > t.dataScadenza) {
    ctx.addIssue({
      code: 'custom',
      message: 'La data di inizio non può essere successiva alla scadenza',
      path: ['dataInizio'],
    });
  }
});
export type TaskCreate = z.infer<typeof taskCreateSchema>;

/**
 * `.partial()` non basta sui campi con `.default(...)` (`stato`,
 * `scadenzaConOrario`): Zod applica comunque il default quando la chiave è
 * assente, quindi un PATCH che li omette li resetterebbe silenziosamente —
 * stesso accorgimento di `progettoUpdateSchema`. La coerenza fase/progetto e
 * l'ordine inizio/scadenza restano verificate nel service:
 * un update parziale può cambiare solo una delle due date o dei due id, e la
 * combinazione effettiva si conosce solo unendo la riga esistente al patch.
 */
export const taskUpdateSchema = taskBaseSchema.partial().extend({
  stato: StatoTask.optional(),
  scadenzaConOrario: z.boolean().optional(),
  /**
   * Sui campi che si possono **svuotare** (togliere l'assegnatario, staccare il
   * task dal progetto, azzerare la priorità) `undefined` non basta: in JSON una
   * chiave con valore `undefined` semplicemente non viene serializzata, quindi
   * sarebbe indistinguibile da "non toccare questo campo". Serve un valore
   * esplicito: `null` = azzera, chiave assente = lascia com'è.
   */
  assegnatarioId: idSelezioneSchema('Seleziona un assegnatario').nullable().optional(),
  progettoId: idSelezioneSchema('Seleziona un progetto').nullable().optional(),
  faseProgettoId: idSelezioneSchema('Seleziona una fase').nullable().optional(),
  dataInizio: dataOraSchema.nullable().optional(),
  priorita: PrioritaTask.nullable().optional(),
  descrizione: z.string().trim().max(10_000).nullable().optional(),
});
export type TaskUpdate = z.infer<typeof taskUpdateSchema>;

/**
 * Il task come lo restituisce l'API. Porta con sé i **nomi** delle entità
 * collegate, non i soli id: la scheda di dettaglio si apre anche da link
 * diretto, dove non c'è nessuna riga di lista da cui ereditarli, e senza
 * questi campi servirebbero tre chiamate in più solo per mostrarli.
 */
export const taskSchema = taskBaseSchema.extend({
  id: idSchema,
  progettoNome: z.string().nullable(),
  progettoCodiceInterno: z.string().nullable(),
  faseNome: z.string().nullable(),
  assegnatarioNome: z.string().nullable(),
  assegnatarioHaImmagine: z.boolean(),
  /** Chi l'ha creato, per la riga di tracciamento in fondo alla scheda. */
  autoreNome: z.string().nullable(),
  completatoIl: dataOraSchema.nullable(),
  creatoDa: idSchema.nullable(),
  creatoIl: dataOraSchema,
});
export type Task = z.infer<typeof taskSchema>;

/**
 * Righe della lista base: i nomi collegati (progetto/fase/assegnatario)
 * evitano un secondo giro di chiamate dal client solo per mostrarli in tabella
 * o nelle card di calendario/timeline — stesso taglio di `ProgettoListItem`.
 */
export const taskListItemSchema = z.object({
  id: idSchema,
  titolo: z.string(),
  progettoId: idSchema.nullable(),
  progettoNome: z.string().nullable(),
  faseProgettoId: idSchema.nullable(),
  faseNome: z.string().nullable(),
  assegnatarioId: idSchema.nullable(),
  assegnatarioNome: z.string().nullable(),
  /** Solo per l'avatar: evita al client un secondo giro per sapere se c'è la foto. */
  assegnatarioHaImmagine: z.boolean(),
  /** Serve alle viste a tempo: con l'inizio il task è una barra, senza è un punto. */
  dataInizio: dataOraSchema.nullable(),
  dataScadenza: dataOraSchema,
  scadenzaConOrario: z.boolean(),
  stato: StatoTask,
  priorita: PrioritaTask.nullable(),
  completatoIl: dataOraSchema.nullable(),
});
export type TaskListItem = z.infer<typeof taskListItemSchema>;

/**
 * Filtro "Attivi" (Da fare o In corso) preselezionato nella lista base.
 * "Scaduti" sono gli attivi con la scadenza passata: non è uno stato salvato,
 * si ricava dalla data, così non può andare fuori sincrono.
 *
 * "Tutti" esiste come valore esplicito e non come semplice assenza del
 * parametro: senza filtro il default dello schema tornerebbe ad "Attivi", e
 * "mostrami tutto" diventerebbe irraggiungibile.
 */
export const taskListQuerySchema = paginationQuerySchema.extend({
  ambito: z.enum(['miei', 'progetto', 'team']).default('miei'),
  stato: z
    .union([StatoTask, z.literal('Attivi'), z.literal('Scaduti'), z.literal('Tutti')])
    .default('Attivi'),
  progettoId: idSchema.optional(),
  faseProgettoId: idSchema.optional(),
  assegnatarioId: idSchema.optional(),
  priorita: PrioritaTask.optional(),
  da: dataOraSchema.optional(),
  a: dataOraSchema.optional(),
});
export type TaskListQuery = z.infer<typeof taskListQuerySchema>;

/** Peso di ordinamento della priorità: Alta → Media → Bassa. */
export const pesoPriorita: Record<z.infer<typeof PrioritaTask>, number> = {
  Alta: 0,
  Media: 1,
  Bassa: 2,
};

/**
 * Ordinamento canonico delle viste a tempo: prima la scadenza, poi la priorità.
 * I task senza orario contano come "all-day" e stanno in cima al loro giorno.
 */
export function confrontaTaskPerUrgenza(
  a: Pick<Task, 'dataScadenza' | 'scadenzaConOrario' | 'priorita'>,
  b: Pick<Task, 'dataScadenza' | 'scadenzaConOrario' | 'priorita'>,
): number {
  const giornoA = a.dataScadenza.slice(0, 10);
  const giornoB = b.dataScadenza.slice(0, 10);
  if (giornoA !== giornoB) return giornoA < giornoB ? -1 : 1;

  if (a.scadenzaConOrario !== b.scadenzaConOrario) return a.scadenzaConOrario ? 1 : -1;
  if (a.scadenzaConOrario && b.scadenzaConOrario && a.dataScadenza !== b.dataScadenza) {
    return a.dataScadenza < b.dataScadenza ? -1 : 1;
  }

  const pesoA = a.priorita ? pesoPriorita[a.priorita] : 3;
  const pesoB = b.priorita ? pesoPriorita[b.priorita] : 3;
  return pesoA - pesoB;
}
